// Time zones a business can pick in Settings > Business. Keep in sync with
// SUPPORTED_BUSINESS_TIME_ZONES in server/utils/businessTimeZones.js.
export const DEFAULT_TIME_ZONE = "America/New_York";

export const TIME_ZONES = [
  ["America/New_York", "Eastern Time (ET)"],
  ["America/Chicago", "Central Time (CT)"],
  ["America/Denver", "Mountain Time (MT)"],
  ["America/Phoenix", "Arizona Time (MST)"],
  ["America/Los_Angeles", "Pacific Time (PT)"],
  ["America/Anchorage", "Alaska Time"],
  ["Pacific/Honolulu", "Hawaii Time"],
];

export const isSupportedTimeZone = (value) =>
  typeof value === "string" && TIME_ZONES.some(([zone]) => zone === value);

export const timeZoneLabel = (value) =>
  TIME_ZONES.find(([zone]) => zone === value)?.[1] || String(value || "").trim();

// The IANA zone this browser reports, or "" when Intl can't say.
export const getBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (error) {
    return "";
  }
};

// The browser zone if a business can use it, otherwise null so callers can
// leave the choice to the server's Eastern default.
export const getSupportedBrowserTimeZone = () => {
  const zone = getBrowserTimeZone();
  return isSupportedTimeZone(zone) ? zone : null;
};

// "Eastern Time (ET)" -> "Eastern Time", for inline sentences.
export const shortTimeZoneLabel = (value) =>
  timeZoneLabel(value).replace(/\s*\([^)]*\)\s*$/, "");

// Booking slots are shown in the business's wall clock, even when the visitor
// opens the page from another zone.
export const bookingTimeZoneNotice = (businessTimeZone = DEFAULT_TIME_ZONE) => {
  const zone = businessTimeZone || DEFAULT_TIME_ZONE;
  const browserZone = getBrowserTimeZone();
  return !browserZone || timeZonesMatch(zone, browserZone)
    ? ""
    : `Times in ${shortTimeZoneLabel(zone)}`;
};

export const bookingWallTimeToInstant = (dateKey, time, timeZone = DEFAULT_TIME_ZONE) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey || "") || !/^\d{1,2}:\d{2}$/.test(time || "")) {
    return null;
  }
  const [year, month, day] = dateKey.split("-").map(Number);
  const [hour, minute] = time.split(":").map(Number);
  if (hour > 23 || minute > 59) return null;
  const wallClock = Date.UTC(year, month - 1, day, hour, minute);
  if (Number.isNaN(wallClock)) return null;
  try {
    // Check both sides of a DST change. The earlier matching instant wins if
    // the clock repeats an hour in autumn.
    const offsets = new Set([-86400000, 0, 86400000].map((delta) =>
      utcOffsetMinutes(timeZone, new Date(wallClock + delta))));
    const candidates = [...offsets]
      .map((offset) => new Date(wallClock - offset * 60000))
      .filter((instant) => {
        const parts = Object.fromEntries(new Intl.DateTimeFormat("en-US", {
          timeZone,
          hourCycle: "h23",
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
        }).formatToParts(instant).map(({ type, value }) => [type, Number(value)]));
        return parts.year === year && parts.month === month && parts.day === day &&
          parts.hour === hour && parts.minute === minute;
      })
      .sort((a, b) => a - b);
    return candidates[0] || null;
  } catch (error) {
    return null;
  }
};

const utcOffsetMinutes = (timeZone, instant) => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    })
      .formatToParts(instant)
      .map(({ type, value }) => [type, Number(value)])
  );
  const wallClock = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
  return Math.round((wallClock - instant.getTime()) / 60000);
};

// Two zones match when they keep the same clock all year — America/Detroit
// and America/New_York do, so a pro in Detroit isn't told their Eastern
// business is in the wrong zone.
export const timeZonesMatch = (first, second) => {
  if (!first || !second) return false;
  if (first === second) return true;
  try {
    const year = new Date().getUTCFullYear();
    return [0, 6].every((month) => {
      const instant = new Date(Date.UTC(year, month, 15, 12));
      return utcOffsetMinutes(first, instant) === utcOffsetMinutes(second, instant);
    });
  } catch (error) {
    return false;
  }
};
